import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Settings as SettingsIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTheme } from '@/components/theme-provider';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/config';
import { PageHeader } from '@/components/PageHeader';

interface UserSettings {
  name: string;
  email: string;
  notifications: {
    email: boolean;
    push: boolean;
  };
  preferences: {
    theme: string;
    language: string;
  };
}

function Settings() {
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false);
  const [editProfile, setEditProfile] = useState({ name: '', email: '' });
  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark' | 'system'>('system');
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>('en');
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      // Fetch user profile
      const profileResponse = await fetch(`${apiUrl}/api/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Fetch user settings
      const settingsResponse = await fetch(`${apiUrl}/api/settings/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (profileResponse.ok && settingsResponse.ok) {
        const profileData = await profileResponse.json();
        const settingsData = await settingsResponse.json();

        // Combine profile and settings data
        const userSettings: UserSettings = {
          name: profileData.data?.name || '',
          email: profileData.data?.email || '',
          notifications: {
            email: settingsData.data?.notifications?.email || false,
            push: settingsData.data?.notifications?.browser || false,
          },
          preferences: {
            theme: settingsData.data?.theme || 'system',
            language: settingsData.data?.language || 'English',
          },
        };

        setSettings(userSettings);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditProfileDialog = () => {
    if (settings) {
      setEditProfile({ name: settings.name, email: settings.email });
      setIsEditProfileOpen(true);
    }
  };

  const handleEditProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editProfile),
      });

      if (response.ok) {
        setIsEditProfileOpen(false);
        fetchSettings();
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  const handleToggleEmailNotifications = async () => {
    if (!settings) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ emailNotifications: !settings.notifications.email }),
      });

      if (response.ok) {
        fetchSettings();
      }
    } catch (error) {
      console.error('Failed to toggle email notifications:', error);
    }
  };

  const handleTogglePushNotifications = async () => {
    if (!settings) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pushNotifications: !settings.notifications.push }),
      });

      if (response.ok) {
        fetchSettings();
      }
    } catch (error) {
      console.error('Failed to toggle push notifications:', error);
    }
  };

  const openThemeDialog = () => {
    setSelectedTheme(theme);
    setIsThemeDialogOpen(true);
  };

  const handleChangeTheme = () => {
    setTheme(selectedTheme);
    setIsThemeDialogOpen(false);
  };

  const openLanguageDialog = () => {
    // Get current language code from i18n
    const currentLang = i18n.language as SupportedLanguage;
    setSelectedLanguage(currentLang in SUPPORTED_LANGUAGES ? currentLang : 'en');
    setIsLanguageDialogOpen(true);
  };

  const handleChangeLanguage = () => {
    // Change the i18n language (this also persists to localStorage via i18n config)
    i18n.changeLanguage(selectedLanguage);
    setIsLanguageDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        icon={SettingsIcon}
        eyebrow="Account"
        title={t('common.settings')}
        subtitle={t('settings.preferences.description')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.profile.title')}</CardTitle>
          <CardDescription>{t('settings.profile.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings ? (
            <>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('users.userName')}</label>
                <p className="text-lg">{settings.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('users.email')}</label>
                <p className="text-lg">{settings.email}</p>
              </div>
              <Button onClick={openEditProfileDialog}>{t('settings.profile.edit')}</Button>
            </>
          ) : (
            <p className="text-center text-muted-foreground">{t('dashboard.noData')}</p>
          )}
        </CardContent>
      </Card>

      {settings && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.notifications.title')}</CardTitle>
              <CardDescription>{t('settings.notifications.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('settings.notifications.email')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notifications.emailDesc')}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleToggleEmailNotifications}>
                  {settings.notifications.email ? t('settings.notifications.enabled') : t('settings.notifications.disabled')}
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('settings.notifications.push')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notifications.pushDesc')}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleTogglePushNotifications}>
                  {settings.notifications.push ? t('settings.notifications.enabled') : t('settings.notifications.disabled')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('settings.preferences.title')}</CardTitle>
              <CardDescription>{t('settings.preferences.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('settings.preferences.theme')}</p>
                  <p className="text-sm text-muted-foreground">{t('settings.preferences.current', { value: theme })}</p>
                </div>
                <Button variant="outline" size="sm" onClick={openThemeDialog}>
                  {t('settings.preferences.changeTheme')}
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('common.language')}</p>
                  <p className="text-sm text-muted-foreground">{t('settings.preferences.current', { value: SUPPORTED_LANGUAGES[i18n.language as SupportedLanguage]?.name || 'English' })}</p>
                </div>
                <Button variant="outline" size="sm" onClick={openLanguageDialog}>
                  {t('settings.preferences.changeLanguage')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Edit Profile Dialog */}
      <Dialog open={isEditProfileOpen} onOpenChange={setIsEditProfileOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.dialogs.editProfile.title')}</DialogTitle>
            <DialogDescription>
              {t('settings.dialogs.editProfile.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('users.userName')}</Label>
              <Input
                id="name"
                value={editProfile.name}
                onChange={(e) => setEditProfile({ ...editProfile, name: e.target.value })}
                placeholder={t('users.userName')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t('users.email')}</Label>
              <Input
                id="email"
                type="email"
                value={editProfile.email}
                onChange={(e) => setEditProfile({ ...editProfile, email: e.target.value })}
                placeholder={t('auth.emailPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditProfileOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditProfile} disabled={!editProfile.name || !editProfile.email}>
              {t('settings.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Theme Dialog */}
      <Dialog open={isThemeDialogOpen} onOpenChange={setIsThemeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.dialogs.changeTheme.title')}</DialogTitle>
            <DialogDescription>
              {t('settings.dialogs.changeTheme.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="theme">{t('settings.preferences.theme')}</Label>
              <Select
                value={selectedTheme}
                onValueChange={(value) => setSelectedTheme(value as 'light' | 'dark' | 'system')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsThemeDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleChangeTheme}>
              {t('settings.preferences.applyTheme')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Language Dialog */}
      <Dialog open={isLanguageDialogOpen} onOpenChange={setIsLanguageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.dialogs.changeLanguage.title')}</DialogTitle>
            <DialogDescription>
              {t('settings.dialogs.changeLanguage.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="language">{t('common.language')}</Label>
              <Select
                value={selectedLanguage}
                onValueChange={(value) => setSelectedLanguage(value as SupportedLanguage)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SUPPORTED_LANGUAGES).map(([code, { name, nativeName }]) => (
                    <SelectItem key={code} value={code}>
                      {name} ({nativeName})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLanguageDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleChangeLanguage}>
              {t('settings.preferences.saveLanguage')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Settings;
